// ContextualTip (Spec §7 / P4) — a dismissible inline hint placed at the point
// of confusion (below a section header, beside a tricky field). Distinct from
// HelpPopover (on-demand) — this is proactive guidance shown until dismissed,
// remembered per-tip in localStorage so it doesn't nag.

import { useState, type ReactNode } from 'react'
import { Lightbulb, X } from 'lucide-react'

interface ContextualTipProps {
  /** stable id — dismissal is remembered per id */
  id: string
  children: ReactNode
  tone?: 'info' | 'neutral'
  /** when false the tip cannot be dismissed (e.g. a required warning) */
  dismissible?: boolean
}

const STORAGE_PREFIX = 'tip-dismissed:'

export default function ContextualTip({ id, children, tone = 'info', dismissible = true }: ContextualTipProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_PREFIX + id) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  function dismiss() {
    setDismissed(true)
    try {
      localStorage.setItem(STORAGE_PREFIX + id, '1')
    } catch {
      /* storage unavailable — dismissal holds for this session only */
    }
  }

  const styles =
    tone === 'neutral'
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-iflow-200 bg-iflow-50 text-iflow-900'

  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${styles}`} role="note">
      <Lightbulb size={16} className="mt-0.5 shrink-0 text-iflow-600" aria-hidden />
      <div className="flex-1 leading-relaxed">{children}</div>
      {dismissible && (
        <button type="button" onClick={dismiss} aria-label="Dismiss tip" className="shrink-0 text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      )}
    </div>
  )
}
